import React, { useState } from 'react';
import { FaWater, FaBug, FaExclamationTriangle, FaCheck, FaComments, FaHandshake } from 'react-icons/fa';

const ConflictResolution = () => {
  const [conflicts] = useState([
    {
      id: 1,
      type: 'water',
      title: 'Water Rights Dispute',
      description: 'Village A and Village B have conflicting irrigation schedules during drought period',
      status: 'active',
      priority: 'high',
      parties: ['Village A', 'Village B'],
      recommendations: [
        'Implement alternating irrigation schedule',
        'Prioritize drought-resistant crops in Village A',
        'Install water-efficient irrigation in Village B'
      ],
      icon: <FaWater />
    },
    {
      id: 2,
      type: 'pest',
      title: 'Stem Borer Migration',
      description: 'Village C rice planting schedule causing pest migration to Village D',
      status: 'resolved',
      priority: 'medium',
      parties: ['Village C', 'Village D'],
      recommendations: [
        'Delay Village C rice planting by 2 weeks',
        'Implement natural predator zones between villages',
        'Coordinate pest management strategies'
      ],
      resolution: 'Village C agreed to delay planting by 2 weeks to break stem borer cycle',
      icon: <FaBug />
    },
    {
      id: 3,
      type: 'land',
      title: 'Land Use Boundary Dispute',
      description: 'Unclear boundaries between Village E and Village F causing resource conflicts',
      status: 'pending',
      priority: 'medium',
      parties: ['Village E', 'Village F'],
      recommendations: [
        'Conduct official land survey',
        'Establish shared buffer zone',
        'Create joint management committee'
      ],
      icon: <FaExclamationTriangle />
    }
  ]);

  const [selectedConflict, setSelectedConflict] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'var(--accent-red)';
      case 'pending': return 'var(--accent-yellow)';
      case 'resolved': return 'var(--accent-green)';
      default: return 'var(--text-color)';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'var(--accent-red)';
      case 'medium': return 'var(--accent-yellow)';
      case 'low': return 'var(--accent-blue)';
      default: return 'var(--text-color)';
    }
  };

  const handleSelectConflict = (conflict) => {
    setSelectedConflict(conflict);
  };

  const handleResolveConflict = (id) => {
    // In a real app, this would open a resolution form
    const conflict = conflicts.find(c => c.id === id);
    showNotification(`Opening resolution form for: ${conflict.title}`, 'info');
  };

  return (
    <div className="conflict-resolution">
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#2196f3',
            color: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 9999
          }}
        >
          {notification.message}
        </div>
      )}

      <div className="section-header">
        <h2>Conflict Resolution Module</h2>
      </div>

      <div className="conflict-container">
        <div className="conflict-list">
          <h3>Resource Conflicts</h3>
          {conflicts.map(conflict => (
            <div 
              key={conflict.id} 
              className={`conflict-item ${selectedConflict?.id === conflict.id ? 'selected' : ''}`}
              onClick={() => handleSelectConflict(conflict)}
            >
              <div className="conflict-icon">{conflict.icon}</div>
              <div className="conflict-summary">
                <h4>{conflict.title}</h4>
                <div className="conflict-meta">
                  <span 
                    className="conflict-status"
                    style={{ color: getStatusColor(conflict.status) }}
                  >
                    {conflict.status}
                  </span>
                  <span 
                    className="conflict-priority"
                    style={{ color: getPriorityColor(conflict.priority) }}
                  >
                    {conflict.priority} priority
                  </span>
                </div>
              </div>
              {conflict.status === 'resolved' && (
                <div className="conflict-resolved-icon">
                  <FaCheck />
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className="conflict-details">
          {selectedConflict ? (
            <>
              <div className="conflict-header">
                <h3>{selectedConflict.title}</h3>
                <div className="conflict-status-badge" style={{ backgroundColor: getStatusColor(selectedConflict.status) }}>
                  {selectedConflict.status}
                </div>
              </div>
              
              <div className="conflict-description">
                <p>{selectedConflict.description}</p>
              </div>
              
              <div className="conflict-parties">
                <h4>Parties Involved</h4>
                <div className="parties-list">
                  {selectedConflict.parties.map((party, index) => (
                    <div key={index} className="party-tag">{party}</div>
                  ))}
                </div>
              </div>
              
              <div className="conflict-recommendations">
                <h4>AI Recommendations</h4>
                <ul className="recommendations-list">
                  {selectedConflict.recommendations.map((rec, index) => (
                    <li key={index} className="recommendation-item">{rec}</li>
                  ))}
                </ul>
              </div>
              
              {selectedConflict.resolution ? (
                <div className="conflict-resolution-info">
                  <h4>Resolution</h4>
                  <div className="resolution-box">
                    <FaHandshake className="resolution-icon" />
                    <p>{selectedConflict.resolution}</p>
                  </div>
                </div>
              ) : (
                <div className="conflict-actions">
                  <button 
                    className="resolve-button"
                    onClick={() => handleResolveConflict(selectedConflict.id)}
                  >
                    <FaHandshake /> Resolve Conflict
                  </button>
                  <button className="mediate-button">
                    <FaComments /> Schedule Mediation
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-conflict-selected">
              <FaHandshake className="empty-icon" />
              <p>Select a conflict to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConflictResolution; 